import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SearchFunctionality = ({ searchQuery, onSearch }) => {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Mock search functionality - in real implementation this would search through document content
  const searchTerms = [
    { term: 'data collection', section: 'privacy', subsection: 'information-collection' },
    { term: 'user rights', section: 'privacy', subsection: 'user-rights' },
    { term: 'cookies', section: 'privacy', subsection: 'cookies' },
    { term: 'payment terms', section: 'terms', subsection: 'payment-terms' },
    { term: 'termination', section: 'terms', subsection: 'termination' },
    { term: 'liability', section: 'terms', subsection: 'limitation-liability' },
    { term: 'intellectual property', section: 'terms', subsection: 'intellectual-property' },
    { term: 'security', section: 'privacy', subsection: 'data-security' },
    { term: 'acceptable use', section: 'terms', subsection: 'acceptable-use' },
    { term: 'service description', section: 'terms', subsection: 'service-description' }
  ];

  useEffect(() => {
    if (localQuery?.trim()?.length > 2) {
      const results = searchTerms?.filter(item =>
        item?.term?.toLowerCase()?.includes(localQuery?.toLowerCase())
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [localQuery]);

  const handleSearch = (e) => {
    e?.preventDefault();
    onSearch(localQuery);
    setShowResults(false);
  };

  const handleResultClick = (result) => {
    // Navigate to specific section
    const element = document.getElementById(result?.subsection);
    if (element) {
      element?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    setShowResults(false);
    setLocalQuery('');
  };

  const clearSearch = () => {
    setLocalQuery('');
    onSearch('');
    setShowResults(false);
  };

  return (
    <div className="relative">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <Icon name="Search" size={16} className="mr-2 text-gray-500" />
          Search Documents
        </h4>
        
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e?.target?.value)}
              placeholder="Search terms, clauses, or topics..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            
            <Icon 
              name="Search" 
              size={16} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
            />
            
            {localQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
          
          <button
            type="submit"
            disabled={!localQuery?.trim()}
            className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Search Documents
          </button>
        </form>

        {/* Search Results Dropdown */}
        {showResults && searchResults?.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
            <div className="p-2">
              <div className="text-xs text-gray-500 mb-2 px-2">
                Found {searchResults?.length} result{searchResults?.length !== 1 ? 's' : ''}
              </div>
              
              {searchResults?.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded transition-colors flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900 capitalize">
                      {result?.term}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      in {result?.section === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
                    </div>
                  </div>
                  
                  <Icon name="ArrowRight" size={14} className="text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {showResults && searchResults?.length === 0 && localQuery?.trim()?.length > 2 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
            <div className="p-4 text-center">
              <Icon name="Search" size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No results found for "{localQuery}"</p>
              <p className="text-xs text-gray-400 mt-1">Try different keywords or browse sections above</p>
            </div>
          </div>
        )}

        {/* Search Tips */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            <p className="font-medium mb-1">Search Tips:</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Try "data collection", "user rights", "cookies"</li>
              <li>• Search "payment", "termination", "liability"</li>
              <li>• Use specific terms for better results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFunctionality;